<template>
  <div class="mb-[216px] flex flex-col md:mb-[224px]">
    <template v-if="imageExperiment">
      <h2 class="heading-2 mb-24 mt-12">
        {{ i18n(translations.introAtcEnhancedImageTitle) }}
      </h2>

      <div class="border-static-brand-mid shadow-short rounded-lg border-1">
        <div class="bg-action-default-brand-mid rounded-t-lg mb-8 text-center">
          <p class="heading-3 p-16">
            {{ i18n(translations.introAtcEnhancedImageSubtitle) }}
          </p>
        </div>

        <RevIllustration
          alt=""
          class="m-auto"
          :height="140"
          src="/img/buyback/viewOffer.svg"
          :width="140"
        />

        <RevList
          class="mb-24 px-16"
          :has-external-borders="false"
          :hide-dividers="true"
          variant="compact"
        >
          <RevListItemStatic
            v-for="(translation, index) in listTranslations"
            :key="index"
          >
            <template #prefix>
              <IconCheckInCircle class="text-static-brand-mid" />
            </template>

            <template #label>
              {{ translation }}
            </template>
          </RevListItemStatic>
        </RevList>
      </div>
    </template>

    <template v-else>
      <div class="flex flex-col justify-center">
        <h2 class="heading-2 mb-24 mt-36 text-center">
          {{ i18n(translations.introAtcEnhancedAnimatedTitle) }}
        </h2>

        <RevCard class="shadow-long px-16 pb-16 pt-12">
          <RevTag
            class="bg-surface-brand-hi mb-16"
            :label="i18n(translations.introAtcEnhancedAnimatedTagText)"
            variant="primary"
          />

          <div class="flex">
            <RevIllustration
              v-if="image"
              :alt="image.description"
              class="!h-[76px] !w-[70px]"
              :height="76"
              :src="image.url"
              :width="70"
            />

            <div class="flex flex-col pl-16">
              <p v-if="productTitle" class="heading-3">{{ productTitle }}</p>

              <p
                v-if="currentPrice"
                class="text-static-brand-mid font-weight-body-2-bold flex text-[42px] leading-[42px] ease-out motion-safe:transition-transform motion-safe:duration-300"
                :class="popCurrentPrice ? 'scale-110' : 'scale-100'"
                data-test="intro-atc-enhanced-price-animation"
              >
                <span>
                  {{
                    i18n.price(currentPrice, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  }}
                </span>
                <span class="heading-2">*</span>
              </p>
            </div>
          </div>
        </RevCard>

        <p class="text-static-default-low caption pt-16">
          {{ i18n(translations.introAtcEnhancedAnimatedCaptionText) }}
        </p>
      </div>
    </template>
  </div>

  <div class="bg-surface-default-mid absolute bottom-0 left-0 right-0 p-24">
    <RevButton
      class="mt-16"
      data-qa="user-yes"
      data-test="user-yes"
      full-width="always"
      variant="secondary"
      @click="emit('next-step')"
    >
      <span class="flex flex-col">
        <span class="heading-3">
          {{ i18n(translations.introYesGetOfferMain) }}
        </span>

        <span class="body-2">
          {{ i18n(translations.introYesGetOfferSecondary) }}
        </span>
      </span>
    </RevButton>

    <RevButton
      class="mx-auto mt-16 block"
      data-qa="user-no"
      data-test="user-no"
      full-width="always"
      variant="secondary"
      @click="emit('close')"
    >
      <span class="flex flex-col">
        <span class="heading-3">
          {{ i18n(translations.introNoRefuseOfferMain) }}
        </span>

        <span class="body-2">
          {{ i18n(translations.introNoRefuseOfferSecondary) }}
        </span>
      </span>
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, toRaw } from 'vue'

import type { Price } from '@backmarket/http-api'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { cloneDeep } from '@backmarket/utils/object/cloneDeep'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { RevList } from '@ds/components/List'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { RevTag } from '@ds/components/Tag'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'

import translations from './Intro.translations'

type IntroAtcEnhancedV2Props = {
  price?: Price
  priceWithoutTradeIn?: Price
  image?: GetProductResponse['images'][number]
  productTitle?: string
}

const { price, image, productTitle, priceWithoutTradeIn } =
  defineProps<IntroAtcEnhancedV2Props>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'next-step'): void
}>()

const i18n = useI18n()
const experiments = useExperiments()

const listTranslations = [
  i18n(translations.introAtcEnhancedImageListItem1),
  i18n(translations.introAtcEnhancedImageListItem2),
  i18n(translations.introAtcEnhancedImageListItem3),
]

const currentPrice = ref<Price | undefined>(
  cloneDeep(toRaw(priceWithoutTradeIn)),
)
const popCurrentPrice = ref(false)

const imageExperiment =
  experiments['experiment.introAtcInteractionV2'] === 'withNewAtcSwapImage'

let rolldownTimeout: ReturnType<typeof setTimeout> | null = null
let popCurrentPriceTimeout: ReturnType<typeof setTimeout> | null = null

onMounted(() => {
  if (imageExperiment) return

  function startRolldownAnimation() {
    if (!priceWithoutTradeIn?.amount) return
    if (!price?.amount) return

    const startTime = Date.now()
    const duration = 1200

    const reducedPrice = parseFloat(price.amount)
    const fullPrice = parseFloat(priceWithoutTradeIn.amount)
    const priceDelta = fullPrice - reducedPrice

    function animate() {
      if (!currentPrice.value) return

      const elapsed = Date.now() - startTime
      let progress = Math.min(elapsed / duration, 1)

      const easeOut = 1 - (1 - progress) ** 3
      const newPrice = Math.round(fullPrice - priceDelta * easeOut)

      // should never happen, but as a failsafe...
      if (newPrice < 0) {
        progress = 1
        currentPrice.value.amount = '0'
      } else {
        currentPrice.value.amount = newPrice.toString()
      }

      if (progress < 1) requestAnimationFrame(animate)
      else {
        popCurrentPrice.value = true

        popCurrentPriceTimeout = setTimeout(() => {
          popCurrentPrice.value = false
        }, 600)
      }
    }

    animate()
  }

  rolldownTimeout = setTimeout(startRolldownAnimation, 300)
})

onUnmounted(() => {
  if (rolldownTimeout) clearTimeout(rolldownTimeout)
  if (popCurrentPriceTimeout) clearTimeout(popCurrentPriceTimeout)
})
</script>
